/**
 * Install state, and the one browser event that makes a real install possible.
 *
 * `beforeinstallprompt` fires **on the origin being installed**, and only on
 * Chromium. Two consequences shape this file and the `/install` route:
 *
 * 1. The install button has to live in the app, not on a marketing site — a
 *    landing page on another domain cannot trigger it. `SUAP-1305` links here.
 * 2. The event fires **once, early**, often before React has mounted. So it is
 *    captured at module load and stashed; a component asking later still finds
 *    it.
 *
 * iOS has no equivalent API at all. Safari installs through Share → Add to Home
 * Screen and there is nothing to call, which is why the route shows steps rather
 * than a button that would do nothing.
 *
 * SUAP-0106.
 */

/** The slice of `BeforeInstallPromptEvent` we use. It is not in lib.dom yet. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type Platform = 'chromium' | 'ios' | 'other'

let deferred: InstallPromptEvent | null = null
const listeners = new Set<() => void>()

/**
 * Whether `appinstalled` has fired since this page loaded.
 *
 * `isInstalled()` cannot answer this and never could: `display-mode: standalone`
 * is a property of the **window**, not the origin, so a browser tab reads `false`
 * for an app that is sitting on the home screen — including one installed a
 * second ago through the browser menu. `appinstalled` is the one signal a tab
 * does get, and it is therefore the only basis on which `/install` may say the
 * install happened. SUAP-0116.
 */
let installedHere = false

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Chrome shows its own mini-infobar unless this is prevented, and we want
    // the prompt to happen on a tap the user chose rather than a banner.
    event.preventDefault()
    deferred = event as InstallPromptEvent
    for (const listener of listeners) listener()
  })

  window.addEventListener('appinstalled', () => {
    deferred = null
    installedHere = true
    for (const listener of listeners) listener()
  })
}

export function canPrompt(): boolean {
  return deferred !== null
}

export function onAvailabilityChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Show the real install dialog. Returns what the user chose, or null if unavailable. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  if (deferred === null) return null
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  // The event is single-use: Chrome will fire a fresh one if it is still
  // installable, so holding the spent one would leave a dead button.
  deferred = null
  for (const listener of listeners) listener()
  return outcome
}

/**
 * The app was installed while this page was open.
 *
 * Deliberately separate from `isInstalled()`, which asks a different question —
 * *is this window the installed app* — and answers `false` in a tab either way.
 * A page that reads only `isInstalled()` cannot distinguish "not installed" from
 * "installed, and you are reading this in a tab", so it must not assert either.
 */
export function installedDuringThisVisit(): boolean {
  return installedHere
}

/** Already running as an installed app. **This window**, not this origin. */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari's own flag, which predates display-mode and is still the only
    // signal there.
    ('standalone' in window.navigator && window.navigator.standalone === true)
  )
}

export function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'other'
  const ua = window.navigator.userAgent
  // iPadOS 13+ reports itself as a Mac; the touch-point count is what separates
  // an iPad from a desktop Safari that cannot install at all.
  const iPadOS = /Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1
  if (/iPhone|iPad|iPod/.test(ua) || iPadOS) return 'ios'
  if (/Chrome|Chromium|Edg|SamsungBrowser/.test(ua) && !/OPR/.test(ua)) return 'chromium'
  return 'other'
}
